// InteractionTable.tsx
import React, { useState, useMemo } from "react";
import { Interaction, InteractionType } from "../../models";
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

interface InteractionTableProps {
  interactions: Interaction[];
  onDeleteInteraction: (interaction: Interaction) => void;
}

const interactionTypes: (InteractionType | "All")[] = [
  "All",
  "cards",
  "meal",
  "visit",
  "text",
  "service",
  "other",
];

const InteractionTable: React.FC<InteractionTableProps> = ({
  interactions,
  onDeleteInteraction,
}) => {
  // Sorting state: sort by one of the three fields.
  const [sortField, setSortField] = useState<"date" | "type" | "notes">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Filtering state
  const [filterType, setFilterType] = useState<InteractionType | "All">("All");
  const [searchText, setSearchText] = useState<string>("");

  // Filter interactions based on type and notes search.
  const filteredInteractions = useMemo(() => {
    return interactions.filter((interaction) => {
      const matchesType =
        filterType === "All" || interaction.type === filterType;
      const matchesNotes = interaction.notes
        .toLowerCase()
        .includes(searchText.toLowerCase());
      return matchesType && matchesNotes;
    });
  }, [interactions, filterType, searchText]);

  // Sort the filtered interactions.
  const sortedInteractions = useMemo(() => {
    const sorted = [...filteredInteractions].sort((a, b) => {
      let comparison = 0;
      if (sortField === "date") {
        comparison =
          new Date(a.date).getTime() - new Date(b.date).getTime();
      } else if (sortField === "type") {
        comparison = a.type.localeCompare(b.type);
      } else if (sortField === "notes") {
        comparison = a.notes.localeCompare(b.notes);
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
    return sorted;
  }, [filteredInteractions, sortField, sortOrder]);

  // Handle clicking a header to sort by that field.
  const handleSort = (field: "date" | "type" | "notes") => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <div>
      {/* Filter Controls */}
      <div className="flex flex-wrap gap-4 mb-4">
        <FormControl variant="outlined" size="small">
          <InputLabel id="filter-type-label">Filter by Type</InputLabel>
          <Select
            labelId="filter-type-label"
            value={filterType}
            onChange={(e) =>
              setFilterType(e.target.value as InteractionType | "All")
            }
            label="Filter by Type"
          >
            {interactionTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Search Notes"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      {/* Interactions Table */}
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-blue-500">
          <tr>
            <th
              className="px-4 py-2 text-white cursor-pointer"
              onClick={() => handleSort("date")}
            >
              Date {sortField === "date" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
            </th>
            <th
              className="px-4 py-2 text-white cursor-pointer"
              onClick={() => handleSort("type")}
            >
              Type {sortField === "type" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
            </th>
            <th
              className="px-4 py-2 text-white cursor-pointer"
              onClick={() => handleSort("notes")}
            >
              Notes {sortField === "notes" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
            </th>
            <th className="px-4 py-2 text-white">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sortedInteractions.length > 0 ? (
            sortedInteractions.map((interaction, idx) => (
              <tr key={idx} className="hover:bg-gray-100">
                <td className="px-4 py-2">
                  {new Date(interaction.date).toLocaleDateString()}
                </td>
                <td className="px-4 py-2 capitalize">{interaction.type}</td>
                <td className="px-4 py-2">{interaction.notes}</td>
                <td className="px-4 py-2">
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => onDeleteInteraction(interaction)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td className="px-4 py-2 text-center" colSpan={4}>
                No interactions found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default InteractionTable;
